import * as React from 'react';
import { BookOpen, Link2, X } from 'lucide-react';
import apiService from '../../services/api';
import { buildPlaybookContextFromRows } from '../../utils/playbookContext';
import './PlaybookPickerModal.css';

type PlaybookParentProject = { id: string; name: string };

/** Whole-project playbook link passed to AgentView. */
export type LinkedPlaybookSelection = {
  projectId: string;
  /** Shown on the composer chip */
  projectLabel: string;
  /** Full text sent as playbook_context (all records: author + paragraph_text). */
  contextBody: string;
  recordCount: number;
};

interface PlaybookPickerModalProps {
  preferredProjectId?: string;
  workflowHint: 'construction_loan' | 'real_estate' | undefined;
  onSelect: (selection: LinkedPlaybookSelection) => void;
  onClose: () => void;
}

export const PlaybookPickerModal: React.FC<PlaybookPickerModalProps> = ({
  preferredProjectId,
  workflowHint,
  onSelect,
  onClose,
}) => {
  const [projects, setProjects] = React.useState<PlaybookParentProject[]>([]);
  const [projectsLoading, setProjectsLoading] = React.useState(true);
  const [projectsError, setProjectsError] = React.useState<string | null>(null);

  const [activeProjectId, setActiveProjectId] = React.useState(preferredProjectId || '');
  const [linking, setLinking] = React.useState(false);
  const [linkError, setLinkError] = React.useState<string | null>(null);

  const backdropRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    setActiveProjectId(preferredProjectId || '');
  }, [preferredProjectId]);

  React.useEffect(() => {
    let cancelled = false;
    setProjectsLoading(true);
    setProjectsError(null);
    apiService
      .getConstructionLoanPlaybookParentProjects()
      .then((res) => {
        if (cancelled) return;
        if (res.success && Array.isArray(res.data)) {
          let list: PlaybookParentProject[] = res.data.map((p) => ({
            id: p.id,
            name: p.name || p.id,
          }));
          const pref = preferredProjectId?.trim();
          if (pref && !list.some((p) => p.id === pref)) {
            list = [
              { id: pref, name: 'Linked project (Word add-in)' },
              ...list,
            ];
          }
          list.sort((a, b) =>
            (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
          );
          setProjects(list);
        } else if (preferredProjectId?.trim()) {
          setProjects([
            { id: preferredProjectId.trim(), name: 'Linked project (Word add-in)' },
          ]);
        } else {
          setProjectsError(res.message || 'Could not load playbook projects');
        }
      })
      .catch((e) => {
        if (cancelled) return;
        const pref = preferredProjectId?.trim();
        if (pref) {
          setProjects([{ id: pref, name: 'Linked project (Word add-in)' }]);
          setProjectsError(null);
        } else {
          setProjectsError(e?.message || 'Could not load playbook projects');
        }
      })
      .finally(() => {
        if (!cancelled) setProjectsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [preferredProjectId]);

  React.useEffect(() => {
    if (projectsLoading || projects.length !== 1) return;
    setActiveProjectId((cur) => (cur ? cur : projects[0].id));
  }, [projectsLoading, projects]);

  React.useEffect(() => {
    setLinkError(null);
  }, [activeProjectId]);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  function handleBackdropClick(e: React.MouseEvent) {
    if (e.target === backdropRef.current) onClose();
  }

  async function handleLinkPlaybook() {
    if (!activeProjectId || linking) return;
    const project = projects.find((p) => p.id === activeProjectId);
    const projectLabel = project?.name || activeProjectId;
    setLinking(true);
    setLinkError(null);
    try {
      const rows = await apiService.listPlaybooksForAgent(activeProjectId, workflowHint);
      if (!rows.length) {
        setLinkError('No playbook records found for this project.');
        return;
      }
      const contextBody = buildPlaybookContextFromRows(projectLabel, rows);
      onSelect({
        projectId: activeProjectId,
        projectLabel,
        contextBody,
        recordCount: rows.length,
      });
      onClose();
    } catch (e: unknown) {
      setLinkError(e instanceof Error ? e.message : 'Failed to load playbook records');
    } finally {
      setLinking(false);
    }
  }

  const showProjectSelect = !projectsLoading && !projectsError && projects.length > 0;
  const canLink = Boolean(activeProjectId) && !linking && !projectsLoading;

  return (
    <div
      className="pb-modal-backdrop"
      ref={backdropRef}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label="Link playbook context"
    >
      <div className="pb-modal">
        <div className="pb-modal-header">
          <BookOpen size={16} aria-hidden />
          <span className="pb-modal-title">Link playbook</span>
          <button
            type="button"
            className="pb-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <p className="pb-modal-hint">
          Choose an available playbook project, then click <strong>Link playbook</strong>.
        </p>

        <div className="pb-modal-project-row">
          <label className="pb-modal-project-label" htmlFor="pb-project-select">
            Project
          </label>
          {projectsLoading ? (
            <span className="pb-modal-project-loading">Loading projects…</span>
          ) : projectsError ? (
            <span className="pb-modal-project-error">{projectsError}</span>
          ) : showProjectSelect ? (
            <select
              id="pb-project-select"
              className="pb-modal-project-select"
              value={activeProjectId}
              onChange={(e) => setActiveProjectId(e.target.value)}
              aria-label="Choose project for playbooks"
            >
              <option value="">Select a project…</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name || p.id}
                </option>
              ))}
            </select>
          ) : (
            <span className="pb-modal-project-error">
              No Construction Loan projects with playbook data yet. Create Past Contracts in the
              dashboard first.
            </span>
          )}
        </div>

        {linkError && <div className="pb-modal-link-error">{linkError}</div>}

        <div className="pb-modal-actions">
          <button
            type="button"
            className="pb-link-playbook-btn"
            disabled={!canLink}
            onClick={() => void handleLinkPlaybook()}
          >
            {linking ? (
              <>Loading records…</>
            ) : (
              <>
                <Link2 size={16} aria-hidden />
                Link playbook
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
